import * as THREE from 'three';
import type { MapHandle, MapName, ThemeOverride } from './types';
import { MapAssemblyError } from './types';
import { assembleSkyforge } from './skyforge';
import { assembleNeonDistrict } from './neon-district';

export interface LoadMapDeps {
  readonly scene: THREE.Scene;
  readonly groups: import('../types').SceneGroups;
  readonly shaderRegistry: import('../shaders/types').ShaderRegistry;
  /** Tests pass stub; production passes createGltfAssetLoader from src/client/assets/gltf-loader.ts. */
  readonly gltfLoader?: { load(url: string): Promise<THREE.Group> };
  /** When false, validates map JSON but skips decoration GLB/crystal/fountain assembly. */
  readonly loadDecorations?: boolean;
  /** Per-field override; wins over theme.json. */
  readonly themeOverride?: Partial<ThemeOverride>;
}

/** Load by name. Reads board.json + theme.json + decorations.json from `maps/<name>/`.
 *  Async: Skyforge GLB loading is async (deps.gltfLoader.load returns Promise).
 *  Throws MapAssemblyError if name unknown or any JSON fails Zod validation. */
export async function loadMap(name: MapName, _deps: LoadMapDeps): Promise<MapHandle> {
  switch (name) {
    case 'skyforge':
      return assembleSkyforge(_deps);
    case 'neon-district':
      return assembleNeonDistrict(_deps);
    default: {
      // TypeScript exhaustiveness guard — name is never here at compile time,
      // but the cast in tests forces the runtime path.
      const exhausted: never = name;
      throw new MapAssemblyError('unknown_map', `Unknown map: ${exhausted}`);
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Re-exports (contract surface from inline spec)
// ─────────────────────────────────────────────────────────────────────────────

export type { MapHandle, MapName, ThemeOverride } from './types';
export { MapAssemblyError } from './types';
export { assembleMapFromData, type MapDataBundle } from './assemble-map';
export type {
  SkyforgeSchema,
  ThemeJSON,
  DecorationsJSON,
  FXJSON,
  CameraJSON,
  CameraStateJSON,
  BridgeSpec,
  DecorationSpec,
} from './schema';
